// import React, { useState, useEffect, useRef } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { Plus, Search, Filter, Users, Calendar, Upload, Download, X, CheckCircle, AlertCircle, FileSpreadsheet } from 'lucide-react';
// import api from '../../services/api';
// import TaskCard from '../common/TaskCard';
// import LoadingSpinner from '../common/LoadingSpinner';
// import { toast } from 'react-toastify';
// import { getUserIdentifier, normalizeUserProfiles } from '../../utils/userProfile';

// const TaskManagement = ({ onSidebarCountsRefresh }) => {
//   const navigate = useNavigate();
//   const [tasks, setTasks] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [activeTab, setActiveTab] = useState('ongoing'); // ongoing, completed
//   const [filters, setFilters] = useState({
//     employeeId: '',
//     priority: '',
//     search: ''
//   });
//   const [employees, setEmployees] = useState([]);
//   const [taskCounts, setTaskCounts] = useState({
//     ongoing: 0,
//     completed: 0
//   });

//   // Bulk upload state
//   const [showBulkModal, setShowBulkModal] = useState(false);
//   const [bulkFile, setBulkFile] = useState(null);
//   const [bulkUploading, setBulkUploading] = useState(false);
//   const [bulkResult, setBulkResult] = useState(null);
//   const [uploadedFileFingerprint, setUploadedFileFingerprint] = useState('');
//   const fileInputRef = useRef(null);

//   const getFileFingerprint = (file) => {
//     if (!file) return '';
//     return `${file.name}::${file.size}::${file.lastModified}`;
//   };

//   useEffect(() => {
//     fetchEmployees();
//   }, []);

//   useEffect(() => {
//     fetchTasks();
//     // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when tab or filter changes
//   }, [activeTab, filters]);

//   useEffect(() => {
//     if (onSidebarCountsRefresh) {
//       onSidebarCountsRefresh();
//     }
//   }, [onSidebarCountsRefresh]);

//   const fetchEmployees = async () => {
//     try {
//       const response = await api.get('/admin/employees');
//       setEmployees(normalizeUserProfiles(response.data.employees || []));
//     } catch (error) {
//       console.error('Error fetching employees:', error);
//     }
//   };

//   const fetchTasks = async () => {
//     try {
//       setLoading(true);
//       const params = {
//         status: activeTab,
//         employeeId: filters.employeeId || undefined,
//         priority: filters.priority || undefined,
//         search: filters.search || undefined,
//         limit: 100
//       };

//       const response = await api.get('/admin/tasks', { params });
//       setTasks(response.data.tasks || []);

//       // Update counts
//       const ongoingResponse = await api.get('/admin/tasks', { 
//         params: { status: 'ongoing', limit: 1 } 
//       });
//       const completedResponse = await api.get('/admin/tasks', { 
//         params: { status: 'completed', limit: 1 } 
//       });

//       setTaskCounts({
//         ongoing: ongoingResponse.data.pagination?.total || 0,
//         completed: completedResponse.data.pagination?.total || 0
//       });
//     } catch (error) {
//       console.error('Error fetching tasks:', error);
//       toast.error('Failed to fetch tasks');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleSearchChange = (e) => {
//     setFilters({ ...filters, search: e.target.value });
//   };

//   const handleFilterChange = (key, value) => {
//     setFilters({ ...filters, [key]: value });
//   };

//   const clearFilters = () => {
//     setFilters({
//       employeeId: '',
//       priority: '',
//       search: ''
//     });
//   };

//   const hasActiveFilters = filters.employeeId || filters.priority || filters.search;

//   const handleDownloadTemplate = async () => {
//     try {
//       const response = await api.get('/admin/tasks/bulk-upload/template', { responseType: 'blob' });
//       const url = window.URL.createObjectURL(new Blob([response.data]));
//       const link = document.createElement('a');
//       link.href = url;
//       link.setAttribute('download', 'task_upload_template.xlsx');
//       document.body.appendChild(link);
//       link.click();
//       link.remove();
//       window.URL.revokeObjectURL(url);
//     } catch (error) {
//       toast.error('Failed to download template');
//     }
//   };

//   const handleBulkUpload = async () => {
//     if (!bulkFile) {
//       toast.error('Please select an Excel file first');
//       return;
//     }

//     const selectedFingerprint = getFileFingerprint(bulkFile);
//     if (uploadedFileFingerprint && uploadedFileFingerprint === selectedFingerprint) {
//       toast.info('This file was already uploaded. Select or modify a file before uploading again.');
//       return;
//     }

//     try {
//       setBulkUploading(true);
//       setBulkResult(null);
//       const formData = new FormData();
//       formData.append('file', bulkFile);
//       const response = await api.post('/admin/tasks/bulk-upload', formData, {
//         headers: { 'Content-Type': 'multipart/form-data' }
//       });
//       setBulkResult(response.data);
//       setUploadedFileFingerprint(selectedFingerprint);
//       if (response.data.summary.created > 0) {
//         toast.success(`${response.data.summary.created} task(s) created successfully`);
//         fetchTasks();
//         if (onSidebarCountsRefresh) onSidebarCountsRefresh();
//       }
//     } catch (error) {
//       const msg = error.response?.data?.message || 'Bulk upload failed';
//       toast.error(msg);
//     } finally {
//       setBulkUploading(false);
//     }
//   };

//   const closeBulkModal = () => {
//     setShowBulkModal(false);
//     setBulkFile(null);
//     setBulkResult(null);
//     setUploadedFileFingerprint('');
//     if (fileInputRef.current) fileInputRef.current.value = '';
//   };

//   const isAlreadyUploadedFile = bulkFile && uploadedFileFingerprint === getFileFingerprint(bulkFile);

//   return (
//     <div className="space-y-6">
//       {/* Header */}
//       <div className="flex items-center justify-between">
//         <div>
//           <h1 className="text-2xl font-bold text-gray-900">Task Management</h1>
//           <p className="text-gray-600 mt-1">Assign and track tasks for your team</p>
//         </div>
//         <div className="flex items-center gap-3">
//           <button
//             onClick={() => setShowBulkModal(true)}
//             className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
//           >
//             <Upload className="h-5 w-5 mr-2 text-gray-500" />
//             Bulk Upload
//           </button>
//           <button
//             onClick={() => navigate('/admin/tasks/create')}
//             className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
//           >
//             <Plus className="h-5 w-5 mr-2" />
//             Create New Task
//           </button>
//         </div>
//       </div>

//       {/* Bulk Upload Modal */}
//       {showBulkModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
//             {/* Modal Header */}
//             <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
//               <div className="flex items-center gap-2">
//                 <FileSpreadsheet className="h-5 w-5 text-primary-600" />
//                 <h2 className="text-lg font-semibold text-gray-900">Bulk Upload Tasks</h2>
//               </div>
//               <button onClick={closeBulkModal} className="text-gray-400 hover:text-gray-600 transition-colors">
//                 <X className="h-5 w-5" />
//               </button>
//             </div>

//             <div className="px-6 py-5 space-y-5">
//               {/* Instructions */}
//               <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
//                 <p className="text-sm font-medium text-blue-800 mb-2">How it works:</p>
//                 <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
//                   <li>Download the Excel template below</li>
//                   <li>Fill in one row per task with Employee ID, title, description, priority, and deadline</li>
//                   <li>Upload the completed file — tasks will be auto-assigned to each employee</li>
//                 </ol>
//                 <p className="text-xs text-blue-600 mt-2">
//                   Required columns: <strong>Employee ID</strong>, <strong>Task Title</strong>, <strong>Task Description</strong> &nbsp;|&nbsp;
//                   Optional: Priority (low/medium/high/urgent), Deadline (YYYY-MM-DD), Cadence (daily/weekly/monthly/yearly)
//                 </p>
//               </div>

//               {/* Download Template */}
//               <button
//                 onClick={handleDownloadTemplate}
//                 className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-primary-400 hover:text-primary-600 transition-colors w-full justify-center"
//               >
//                 <Download className="h-4 w-4" />
//                 Download Excel Template
//               </button>

//               {/* File Upload */}
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">Upload Excel File</label>
//                 <div
//                   onClick={() => fileInputRef.current?.click()}
//                   className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
//                     bulkFile ? 'border-primary-400 bg-primary-50' : 'border-gray-300 hover:border-primary-400'
//                   }`}
//                 >
//                   {bulkFile ? (
//                     <div className="flex items-center justify-center gap-2 text-primary-700">
//                       <FileSpreadsheet className="h-5 w-5" />
//                       <span className="text-sm font-medium">{bulkFile.name}</span>
//                       <button
//                         onClick={(e) => { e.stopPropagation(); setBulkFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
//                         className="ml-1 text-gray-400 hover:text-gray-600"
//                       >
//                         <X className="h-4 w-4" />
//                       </button>
//                     </div>
//                   ) : (
//                     <div className="text-gray-500">
//                       <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
//                       <p className="text-sm">Click to select an Excel file (.xlsx, .xls)</p>
//                     </div>
//                   )}
//                 </div>
//                 <input
//                   ref={fileInputRef}
//                   type="file"
//                   accept=".xlsx,.xls"
//                   className="hidden"
//                   onChange={(e) => {
//                     setBulkFile(e.target.files[0] || null);
//                     setBulkResult(null);
//                   }}
//                 />
//               </div>

//               {/* Upload Button */}
//               <button
//                 onClick={handleBulkUpload}
//                 disabled={!bulkFile || bulkUploading || isAlreadyUploadedFile}
//                 className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
//               >
//                 {bulkUploading ? (
//                   <>
//                     <LoadingSpinner size="sm" />
//                     Uploading...
//                   </>
//                 ) : isAlreadyUploadedFile ? (
//                   <>
//                     <CheckCircle className="h-4 w-4" />
//                     Already Uploaded - Select Another File
//                   </>
//                 ) : (
//                   <>
//                     <Upload className="h-4 w-4" />
//                     Upload & Create Tasks
//                   </>
//                 )}
//               </button>

//               {/* Results */}
//               {bulkResult && (
//                 <div className="space-y-3">
//                   {/* Summary */}
//                   <div className="flex gap-3">
//                     <div className="flex-1 bg-green-50 border border-green-200 rounded-lg p-3 text-center">
//                       <p className="text-2xl font-bold text-green-700">{bulkResult.summary.created}</p>
//                       <p className="text-xs text-green-600 mt-0.5">Tasks Created</p>
//                     </div>
//                     {bulkResult.summary.skipped > 0 && (
//                       <div className="flex-1 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
//                         <p className="text-2xl font-bold text-yellow-700">{bulkResult.summary.skipped}</p>
//                         <p className="text-xs text-yellow-600 mt-0.5">Skipped (Duplicates)</p>
//                       </div>
//                     )}
//                     <div className="flex-1 bg-red-50 border border-red-200 rounded-lg p-3 text-center">
//                       <p className="text-2xl font-bold text-red-700">{bulkResult.summary.errors}</p>
//                       <p className="text-xs text-red-600 mt-0.5">Errors</p>
//                     </div>
//                     <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
//                       <p className="text-2xl font-bold text-gray-700">{bulkResult.summary.total}</p>
//                       <p className="text-xs text-gray-600 mt-0.5">Total Rows</p>
//                     </div>
//                   </div>

//                   {/* Created list */}
//                   {bulkResult.details?.created?.length > 0 && (
//                     <div>
//                       <p className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
//                         <CheckCircle className="h-4 w-4 text-green-500" /> Created Tasks
//                       </p>
//                       <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
//                         {bulkResult.details.created.map((item, idx) => (
//                           <div key={idx} className="px-3 py-2 text-xs flex justify-between">
//                             <span className="text-gray-800 font-medium">{item.title}</span>
//                             <span className="text-gray-500">{item.employeeId} – {item.employeeName}</span>
//                           </div>
//                         ))}
//                       </div>
//                     </div>
//                   )}

//                   {/* Errors list */}
//                   {bulkResult.details?.errors?.length > 0 && (
//                     <div>
//                       <p className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
//                         <AlertCircle className="h-4 w-4 text-red-500" /> Errors
//                       </p>
//                       <div className="max-h-40 overflow-y-auto border border-red-200 rounded-lg divide-y divide-red-100">
//                         {bulkResult.details.errors.map((item, idx) => (
//                           <div key={idx} className="px-3 py-2 text-xs">
//                             <span className="text-red-600 font-medium">Row {item.row}:</span>{' '}
//                             <span className="text-red-500">{item.reason}</span>
//                           </div>
//                         ))}
//                       </div>
//                     </div>
//                   )}
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Tabs */}
//       <div className="bg-white rounded-lg shadow">
//         <div className="border-b border-gray-200">
//           <nav className="flex -mb-px">
//             <button
//               onClick={() => setActiveTab('ongoing')}
//               className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
//                 activeTab === 'ongoing'
//                   ? 'border-primary-500 text-primary-600'
//                   : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
//               }`}
//             >
//               Ongoing Tasks
//               {taskCounts.ongoing > 0 && (
//                 <span className="ml-2 px-2 py-0.5 bg-primary-100 text-primary-600 rounded-full text-xs">
//                   {taskCounts.ongoing}
//                 </span>
//               )}
//             </button>
//             <button
//               onClick={() => setActiveTab('completed')}
//               className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
//                 activeTab === 'completed'
//                   ? 'border-primary-500 text-primary-600'
//                   : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
//               }`}
//             >
//               Completed Tasks
//               {taskCounts.completed > 0 && (
//                 <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
//                   {taskCounts.completed}
//                 </span>
//               )}
//             </button>
//           </nav>
//         </div>

//         {/* Filters */}
//         <div className="p-4 border-b border-gray-200 bg-gray-50">
//           <div className="flex flex-col md:flex-row gap-4">
//             {/* Search */}
//             <div className="flex-1">
//               <div className="relative">
//                 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
//                 <input
//                   type="text"
//                   placeholder="Search tasks by title or description..."
//                   value={filters.search}
//                   onChange={handleSearchChange}
//                   className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
//                 />
//               </div>
//             </div>

//             {/* Employee Filter */}
//             <div className="w-full md:w-48">
//               <div className="relative">
//                 <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
//                 <select
//                   value={filters.employeeId}
//                   onChange={(e) => handleFilterChange('employeeId', e.target.value)}
//                   className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none bg-white"
//                 >
//                   <option value="">All Employees</option>
//                   {employees.map(emp => (
//                     <option key={getUserIdentifier(emp) || emp.email} value={getUserIdentifier(emp) || ''}>
//                       {emp.name}
//                     </option>
//                   ))}
//                 </select>
//               </div>
//             </div>

//             {/* Priority Filter */}
//             <div className="w-full md:w-40">
//               <div className="relative">
//                 <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
//                 <select
//                   value={filters.priority}
//                   onChange={(e) => handleFilterChange('priority', e.target.value)}
//                   className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none bg-white"
//                 >
//                   <option value="">All Priorities</option>
//                   <option value="low">Low</option>
//                   <option value="medium">Medium</option>
//                   <option value="high">High</option>
//                   <option value="urgent">Urgent</option>
//                 </select>
//               </div>
//             </div>

//             {/* Clear Filters */}
//             {hasActiveFilters && (
//               <button
//                 onClick={clearFilters}
//                 className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
//               >
//                 Clear Filters
//               </button>
//             )}
//           </div>
//         </div>

//         {/* Task List */}
//         <div className="p-6">
//           {loading ? (
//             <div className="flex justify-center py-12">
//               <LoadingSpinner size="lg" />
//             </div>
//           ) : tasks.length > 0 ? (
//             <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
//               {tasks.map((task) => (
//                 <TaskCard 
//                   key={task._id} 
//                   task={task} 
//                   userRole="admin"
//                 />
//               ))}
//             </div>
//           ) : (
//             <div className="text-center py-12">
//               <Calendar className="mx-auto h-16 w-16 text-gray-400 mb-4" />
//               <h3 className="text-lg font-medium text-gray-900 mb-1">
//                 {hasActiveFilters ? 'No tasks match your filters' : 'No tasks yet'}
//               </h3>
//               <p className="text-gray-600 mb-4">
//                 {hasActiveFilters 
//                   ? 'Try adjusting your filters to see more tasks' 
//                   : activeTab === 'ongoing' 
//                     ? 'Create your first task to get started'
//                     : 'No completed tasks yet'
//                 }
//               </p>
//               {!hasActiveFilters && activeTab === 'ongoing' && (
//                 <button
//                   onClick={() => navigate('/admin/tasks/create')}
//                   className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
//                 >
//                   <Plus className="h-5 w-5 mr-2" />
//                   Create Task
//                 </button>
//               )}
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default TaskManagement;

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Users, Calendar, Upload, Download, X, CheckCircle, AlertCircle, FileSpreadsheet } from 'lucide-react';
import api, { uploadRequestConfig } from '../../services/api';
import TaskCard from '../common/TaskCard';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';
import { getUserIdentifier, normalizeUserProfiles } from '../../utils/userProfile';

const TaskManagement = ({ onSidebarCountsRefresh }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ongoing'); // ongoing, completed
  const [filters, setFilters] = useState({
    employeeId: '',
    priority: '',
    search: ''
  });
  const [employees, setEmployees] = useState([]);
  const [taskCounts, setTaskCounts] = useState({
    ongoing: 0,
    completed: 0
  });

  // Bulk upload state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [uploadedFileFingerprint, setUploadedFileFingerprint] = useState('');
  const fileInputRef = useRef(null);

  const getFileFingerprint = (file) => {
    if (!file) return '';
    return `${file.name}::${file.size}::${file.lastModified}`;
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when tab or filter changes
  }, [activeTab, filters]);

  useEffect(() => {
    if (onSidebarCountsRefresh) {
      onSidebarCountsRefresh();
    }
  }, [onSidebarCountsRefresh]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/admin/employees');
      setEmployees(normalizeUserProfiles(response.data.employees || []));
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params = {
        status: activeTab,
        employeeId: filters.employeeId || undefined,
        priority: filters.priority || undefined,
        search: filters.search || undefined,
        limit: 100
      };

      const response = await api.get('/admin/tasks', { params });
      setTasks(response.data.tasks || []);

      // Update counts
      const ongoingResponse = await api.get('/admin/tasks', { 
        params: { status: 'ongoing', limit: 1 } 
      });
      const completedResponse = await api.get('/admin/tasks', { 
        params: { status: 'completed', limit: 1 } 
      });

      setTaskCounts({
        ongoing: ongoingResponse.data.pagination?.total || 0,
        completed: completedResponse.data.pagination?.total || 0
      });
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setFilters({ ...filters, search: e.target.value });
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    setFilters({
      employeeId: '',
      priority: '',
      search: ''
    });
  };

  const hasActiveFilters = filters.employeeId || filters.priority || filters.search;

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/admin/tasks/bulk-upload/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'task_upload_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      toast.error('Please select an Excel file first');
      return;
    }

    const selectedFingerprint = getFileFingerprint(bulkFile);
    if (uploadedFileFingerprint && uploadedFileFingerprint === selectedFingerprint) {
      toast.info('This file was already uploaded. Select or modify a file before uploading again.');
      return;
    }

    try {
      setBulkUploading(true);
      setBulkResult(null);
      const formData = new FormData();
      formData.append('file', bulkFile);
      const response = await api.post('/admin/tasks/bulk-upload', formData, uploadRequestConfig());
      setBulkResult(response.data);
      setUploadedFileFingerprint(selectedFingerprint);
      if (response.data.summary.created > 0) {
        toast.success(`${response.data.summary.created} task(s) created successfully`);
        fetchTasks();
        if (onSidebarCountsRefresh) onSidebarCountsRefresh();
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Bulk upload failed';
      toast.error(msg);
    } finally {
      setBulkUploading(false);
    }
  };

  const closeBulkModal = () => {
    setShowBulkModal(false);
    setBulkFile(null);
    setBulkResult(null);
    setUploadedFileFingerprint('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const isAlreadyUploadedFile = bulkFile && uploadedFileFingerprint === getFileFingerprint(bulkFile);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Task Management</h1>
          <p className="text-gray-600 mt-1">Assign and track tasks for your team</p>
        </div>
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 w-full md:w-auto">
          <button
            onClick={() => setShowBulkModal(true)}
            className="w-full md:w-auto flex items-center justify-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          >
            <Upload className="h-5 w-5 mr-2 text-gray-500" />
            Bulk Upload
          </button>
          <button
            onClick={() => navigate('/admin/tasks/create')}
            className="w-full md:w-auto flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create New Task
          </button>
        </div>
      </div>

      {/* Bulk Upload Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-primary-600" />
                <h2 className="text-lg font-semibold text-gray-900">Bulk Upload Tasks</h2>
              </div>
              <button onClick={closeBulkModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-800 mb-2">How it works:</p>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Download the Excel template below</li>
                  <li>Fill in one row per task with Employee ID, title, description, priority, and deadline</li>
                  <li>Upload the completed file — tasks will be auto-assigned to each employee</li>
                </ol>
                <p className="text-xs text-blue-600 mt-2">
                  Required columns: <strong>Employee ID</strong>, <strong>Task Title</strong>, <strong>Task Description</strong> &nbsp;|&nbsp;
                  Optional: Priority (low/medium/high/urgent), Deadline (YYYY-MM-DD), Cadence (daily/weekly/monthly/yearly)
                </p>
              </div>

              {/* Download Template */}
              <button
                onClick={handleDownloadTemplate}
                className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-primary-400 hover:text-primary-600 transition-colors w-full justify-center"
              >
                <Download className="h-4 w-4" />
                Download Excel Template
              </button>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload Excel File</label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    bulkFile ? 'border-primary-400 bg-primary-50' : 'border-gray-300 hover:border-primary-400'
                  }`}
                >
                  {bulkFile ? (
                    <div className="flex items-center justify-center gap-2 text-primary-700">
                      <FileSpreadsheet className="h-5 w-5" />
                      <span className="text-sm font-medium">{bulkFile.name}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setBulkFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                        className="ml-1 text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-gray-500">
                      <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm">Click to select an Excel file (.xlsx, .xls)</p>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => {
                    setBulkFile(e.target.files[0] || null);
                    setBulkResult(null);
                  }}
                />
              </div>

              {/* Upload Button */}
              <button
                onClick={handleBulkUpload}
                disabled={!bulkFile || bulkUploading || isAlreadyUploadedFile}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {bulkUploading ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Uploading...
                  </>
                ) : isAlreadyUploadedFile ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Already Uploaded - Select Another File
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Upload & Create Tasks
                  </>
                )}
              </button>

              {/* Results */}
              {bulkResult && (
                <div className="space-y-3">
                  {/* Summary */}
                  <div className="flex gap-3">
                    <div className="flex-1 bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-700">{bulkResult.summary.created}</p>
                      <p className="text-xs text-green-600 mt-0.5">Tasks Created</p>
                    </div>
                    {bulkResult.summary.skipped > 0 && (
                      <div className="flex-1 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-yellow-700">{bulkResult.summary.skipped}</p>
                        <p className="text-xs text-yellow-600 mt-0.5">Skipped (Duplicates)</p>
                      </div>
                    )}
                    <div className="flex-1 bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-700">{bulkResult.summary.errors}</p>
                      <p className="text-xs text-red-600 mt-0.5">Errors</p>
                    </div>
                    <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-gray-700">{bulkResult.summary.total}</p>
                      <p className="text-xs text-gray-600 mt-0.5">Total Rows</p>
                    </div>
                  </div>

                  {/* Created list */}
                  {bulkResult.details?.created?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                        <CheckCircle className="h-4 w-4 text-green-500" /> Created Tasks
                      </p>
                      <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                        {bulkResult.details.created.map((item, idx) => (
                          <div key={idx} className="px-3 py-2 text-xs flex justify-between">
                            <span className="text-gray-800 font-medium">{item.title}</span>
                            <span className="text-gray-500">{item.employeeId} – {item.employeeName}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Errors list */}
                  {bulkResult.details?.errors?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4 text-red-500" /> Errors
                      </p>
                      <div className="max-h-40 overflow-y-auto border border-red-200 rounded-lg divide-y divide-red-100">
                        {bulkResult.details.errors.map((item, idx) => (
                          <div key={idx} className="px-3 py-2 text-xs">
                            <span className="text-red-600 font-medium">Row {item.row}:</span>{' '}
                            <span className="text-red-500">{item.reason}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('ongoing')}
              className={`px-4 md:px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'ongoing'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Ongoing Tasks
              {taskCounts.ongoing > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-primary-100 text-primary-600 rounded-full text-xs">
                  {taskCounts.ongoing}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`px-4 md:px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'completed'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Completed Tasks
              {taskCounts.completed > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                  {taskCounts.completed}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search tasks by title or description..."
                  value={filters.search}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Employee + Priority: 2-column grid on mobile, normal row items on desktop */}
            <div className="grid grid-cols-2 gap-4 md:contents">
              {/* Employee Filter */}
              <div className="md:w-48">
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <select
                    value={filters.employeeId}
                    onChange={(e) => handleFilterChange('employeeId', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none bg-white"
                  >
                    <option value="">All Employees</option>
                    {employees.map(emp => (
                      <option key={getUserIdentifier(emp) || emp.email} value={getUserIdentifier(emp) || ''}>
                        {emp.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Priority Filter */}
              <div className="md:w-40">
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <select
                    value={filters.priority}
                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none bg-white"
                  >
                    <option value="">All Priorities</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="w-full md:w-auto px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Task List */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : tasks.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {tasks.map((task) => (
                <TaskCard 
                  key={task._id} 
                  task={task} 
                  userRole="admin"
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="mx-auto h-16 w-16 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                {hasActiveFilters ? 'No tasks match your filters' : 'No tasks yet'}
              </h3>
              <p className="text-gray-600 mb-4">
                {hasActiveFilters 
                  ? 'Try adjusting your filters to see more tasks' 
                  : activeTab === 'ongoing' 
                    ? 'Create your first task to get started'
                    : 'No completed tasks yet'
                }
              </p>
              {!hasActiveFilters && activeTab === 'ongoing' && (
                <button
                  onClick={() => navigate('/admin/tasks/create')}
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Create Task
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskManagement;
